import { IsFlagActive } from './visible-checks';

/**
 * Per-user Backstage feature flag that gates the automated remediation
 * ("Apply Endor Action") UI for Endor SCA findings.
 *
 * Unlike {@link GATED_MEASUREMENTS}, this does not hide a whole measurement —
 * SCA findings are always listed. It only controls whether the auto-remediation
 * badge/button is offered for those findings. Remediation for every other
 * finding source (CodeQL, DigitSec, secret scanning, Wiz, …) is unaffected.
 *
 * Registered in the yardstick-react plugin `featureFlags` array so it appears in
 * `/settings/feature-flags` at app bootstrap (OFF by default for all users).
 * Authored to satisfy Backstage's `validateFlagName` (`/^[a-z]+[a-z0-9-]+$/`).
 */
export const SCA_REMEDIATION_FLAG_KEY = 'ys-appsec-sca-remediation';

/**
 * The backend check id used for Endor SCA findings (registered by
 * `yardstick-appsec-backend` as `sca-checks`). A finding's `from` / `checkId`
 * carries this value, so it is the discriminator the frontend uses to tell an
 * SCA finding apart from other sources.
 */
export const SCA_CHECK_ID = 'sca-checks';

/**
 * Whether a finding originates from the Endor SCA check, based on its
 * `from` / `checkId`. Case-insensitive and tolerant of an undefined id.
 */
export function isScaCheck(checkId: string | undefined | null): boolean {
  return (checkId ?? '').toLowerCase() === SCA_CHECK_ID;
}

/**
 * The single rule for whether the automated-remediation UI (badge/button/tab)
 * should be shown for a finding.
 *
 * Remediation is offered whenever the finding has an autofix available, EXCEPT
 * for Endor SCA findings, which additionally require the
 * {@link SCA_REMEDIATION_FLAG_KEY} feature flag to be enabled for the current
 * user. In other words: show for everything with an autofix, but SCA/Endor only
 * when the flag is toggled on.
 */
export function shouldShowRemediation(
  params: { hasAutoFix: boolean; checkId: string | undefined | null },
  isFlagActive: IsFlagActive,
): boolean {
  const { hasAutoFix, checkId } = params;
  if (!hasAutoFix) {
    return false;
  }
  if (isScaCheck(checkId)) {
    return isFlagActive(SCA_REMEDIATION_FLAG_KEY);
  }
  return true;
}
